import type { IFileUploadLocation } from '../Model/File/IFileUploadLocation.js'

export type SimpleFile = {
  fileName: string
  poolId: string
  orderId: string
  id: string
}

type FileMapEntry = SimpleFile[] | 'LOADING'

export class FileMap {
  private readonly byOrder: Record<string, Record<string, SimpleFile>>
  public readonly byPool: Record<string, FileMapEntry>

  constructor(byOrder: Record<string, Record<string, SimpleFile>> = {}, byPool: Record<string, FileMapEntry> = {}) {
    this.byOrder = byOrder
    this.byPool = byPool
  }

  poolIsLoading(poolId?: string): boolean {
    if (poolId) return this.byPool[poolId] === 'LOADING'
    return Object.values(this.byPool).some((x) => x === 'LOADING')
  }

  findDuplicate(orderId: string, poolId: string, fileName: string): SimpleFile | undefined | boolean {
    const container = this.byPool[poolId]
    if (container === 'LOADING') {
      return true
    }
    if (!container) {
      return undefined
    }
    return container.find((x) => x.fileName === fileName)
  }

  /**
   * Names of the files already uploaded to `poolId`. Used to judge R1/R2 completeness of a new
   * selection against what the pool already holds — a retry may legitimately contain only one half
   * of a pair. Empty while the pool is still loading (adding is blocked in that window anyway).
   */
  fileNamesInPool(poolId: string): string[] {
    const container = this.byPool[poolId]
    if (!container || container === 'LOADING') return []
    return container.map((x) => x.fileName)
  }

  startLoading(poolId: string): FileMap {
    return new FileMap(
      {
        ...this.byOrder
      },
      {
        ...this.byPool,
        [poolId]: 'LOADING'
      }
    )
  }

  poolLoaded(orderId: string, poolId: string, data: IFileUploadLocation[]): FileMap {
    const prev = this.byOrder[orderId] ?? {}
    const filtered = Object.fromEntries(Object.entries(prev).filter(([, value]) => value.poolId !== poolId))
    return new FileMap(
      {
        ...this.byOrder,
        [orderId]: {
          ...filtered,
          ...data.reduce((acc, x) => ({ ...acc, [x.fileName]: { fileName: x.fileName, poolId: poolId, orderId: orderId } }), {})
        }
      },
      {
        ...this.byPool,
        [poolId]: data
      }
    )
  }

  fileLoaded(orderId: string, poolId: string, fileName: string, id: string): FileMap {
    let newByPool: FileMapEntry
    if (this.byPool[poolId] === 'LOADING') {
      newByPool = 'LOADING'
    } else if (this.byPool[poolId]) {
      newByPool = [
        ...this.byPool[poolId],
        {
          fileName: fileName,
          poolId: poolId,
          orderId: orderId,
          id: id
        }
      ]
    } else {
      newByPool = [
        {
          fileName: fileName,
          poolId: poolId,
          orderId: orderId,
          id: id
        }
      ]
    }

    return new FileMap(
      {
        ...this.byOrder,
        [orderId]: {
          ...(this.byOrder[orderId] ?? {}),
          [fileName]: { fileName, poolId, orderId, id }
        }
      },
      {
        ...this.byPool,
        [poolId]: newByPool
      }
    )
  }
}
